import axiosInstance from 'config/axiosInstance';
import React, { useEffect, useState } from 'react';
import AddNewPopUp, { Field } from 'reusable/AddEditDelete/AddNew';
import Export from 'reusable/export';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import ErrorPopup from 'reusable/ErrorPopup';
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onPageChange: (value: number) => void;
  driverData: any[];
}

const Toolbar: React.FC<ToolbarProps> = ({
  sortValue,
  onSearchChange,
  onSortChange,
  onPageChange,
  driverData,
}) => {
  const { t } = useTranslation('driver');
  const [device, setDevice] = useState<any[]>([]);
  const [driverResponse, setDriverResponse] = useState<any>({});
  const [errorPopupVisible, setErrorPopupVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.driver-id', { ns: 'common' }), value: 'id' },
  ];

  // Field key mapping to match formData fields with the expected API keys
  const fieldKeyMap: { [key: string]: string } = {
    [t('add-driver.driver-name')]: 'DRIVER NAME',
    [t('add-driver.email')]: 'EMAIL ADDRESS',
    [t('add-driver.dob')]: 'DATE OF BIRTH',
    [t('add-driver.assigned-device')]: 'ASSIGNED DEVICE',
    [t('add-driver.contact')]: 'CONTACT NUMBER',
  };

  useEffect(() => {
    fetchDeviceData();
  }, []);

  const fetchDeviceData = async () => {
    let apiUrl = `/v1/devices/`;

    try {
      const responseDeviceCount = await axiosInstance.get(apiUrl);
      apiUrl += `?page_size=${responseDeviceCount.data.count}`;
      const responseDevice = await axiosInstance.get(apiUrl);
      const availableDevices = responseDevice.data.results.filter(
        (device: any) => device.user === null
      );
      setDevice(availableDevices);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const AddNewDriverAPI = async (formData: { [key: string]: any }) => {
    // Transform formData to use keys expected by the API
    const transformedData = Object.keys(formData).reduce((acc, label) => {
      const key = fieldKeyMap[label] || label; // Use mapped key or default to label
      acc[key] = formData[label];
      return acc;
    }, {} as { [key: string]: any });

    try {
      let apiUrl = '/v1/drivers/';

      const payload: any = {
        name: transformedData['DRIVER NAME'],
        display_name: transformedData['DRIVER NAME'],
        birthday: transformedData['DATE OF BIRTH'],
        phone: transformedData['CONTACT NUMBER'],
        email: transformedData['EMAIL ADDRESS'],
        devices: transformedData['ASSIGNED DEVICE']?.length > 0
          ? transformedData['ASSIGNED DEVICE'].map((device: any) => device.id)
          : [],
      };

      const response = await axiosInstance.post(apiUrl, payload);
      setDriverResponse(response.data);

      if (transformedData['ASSIGNED DEVICE']?.length > 0) {
        const patchPromises = transformedData['ASSIGNED DEVICE'].map((device: any) => {
          return axiosInstance.patch(`/v1/devices/${device.id}/`, {
            status: 'using',
            user: response.data.id,
          });
        });
        await Promise.all(patchPromises);
        console.log('Driver updated successfully.');
      }
      window.location.reload();
      
    } catch (error: any) {
      if (error.response && error.response.data) {
        const errorMessagesList = Object.entries(error.response.data)
          .map(([field, messages]: [string, any]) => `${field}: ${messages.join(', ')}`);
        setErrorMessages(errorMessagesList);
        setErrorPopupVisible(true);
      } else {
        setErrorMessages(['An unknown error occurred.']);
        setErrorPopupVisible(true);
      }
      console.error('Failed to post driver data or update devices', error);
    }
  };

  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1); 
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    onPageChange(1); 
  };

  const exportFields = [
    { header: 'Driver ID', field: 'id' },
    { header: 'Driver Name', field: 'name' },
    { header: 'Exceed Limits', field: 'exceed' },
    { header: 'Contact Number', field: 'phone' },
    { header: 'Email Address', field: 'email' },
    { header: 'Registered Date', field: 'create_time' },
  ];

  const deviceOptions = device.map((device: any) => ({
    name: device.name,
    id: device.id,
  }));

  const title = `${t('add-driver.title')}`;
  const save = `${t('add-driver.add')}`;
  const fields: Field[] = [
    { type: 'text', label: `${t('add-driver.driver-name')}`, required: true },
    { type: 'email', label: `${t('add-driver.email')}`, required: true },
    { type: 'calendar', label: `${t('add-driver.dob')}`, required: true },
    {
      type: 'multi-select',
      label: `${t('add-driver.assigned-device')}`,
      options: deviceOptions,
      required: false,
    },
    { type: 'text', label: `${t('add-driver.contact')}`, required: false },
  ];

  const handleSave = (AddNewValues: { [key: string]: any }) => {
    AddNewDriverAPI(AddNewValues);
  };

  return (
    <section className="flex flex-row justify-between w-full">
      <div className="flex w-2/5 justify-start space-x-2">
        <Search
          placeholder={t('table.search')}
          onSearchChange={handleSearchChange}
        />
      </div>
      <div className="flex flex-row space-x-2 items-center">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <AddNewPopUp
          label={title}
          title={title}
          fields={fields}
          save={save}
          onSave={handleSave}
        />
        <Export data={driverData} exportFields={exportFields} />
      </div>

      {/* Error Popup */}
      {errorPopupVisible && (
        <ErrorPopup
          title="Error"
          messages={errorMessages}
          onClose={() => setErrorPopupVisible(false)}
        />
      )}
    </section>
  );
};

export default Toolbar;